// Shows a running program as its graphics mode asks (runner.js): a windowed
// mode (Graphics w,h,d,2, or 0 in a debug build, as on the desktop) in a
// window of its size over the IDE, and a full screen one (1, or 0 in a release
// build) full screen, from the program pane, to which Esc returns it. A GUI
// program, which draws its own windows, stays in the program pane. With Keep
// program docked (docked), every program starts in the pane, and a full
// screen one offers full screen there.
//
// The run frame never moves in the page, which would reload it: a window is
// the same frame, placed over the page, with a title bar beside it. Full
// screen needs a click or key press moments before (browsers' transient user
// activation); Run and F5 are one, and otherwise the window offers a button.

const TITLE_BAR = 30, MARGIN = 12;
const PLACE = "b3d-ide:program-window", DOCKED = "b3d-ide:program-docked";

const remember = {
  get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } },
  set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} },
};

const element = (tag, className, text) => {
  const made = document.createElement(tag);
  if (className) made.className = className;
  if (text !== undefined) made.textContent = text;
  return made;
};

const button = (className, label, title, action) => {
  const made = element("button", className, label);
  made.type = "button";
  made.title = title;
  made.setAttribute("aria-label", title);
  made.addEventListener("click", (event) => {
    event.stopPropagation();
    action();
  });
  return made;
};

export class ProgramWindow {
  // pane: the program pane; onClose: stops the program. onFloat(floating)
  // hears where the program is each time it is placed: in a window of its
  // own, or in the pane, full screen or not.
  constructor({ pane, onClose, onFloat = () => {} }) {
    this.pane = pane;
    this.onClose = onClose;
    this.onFloat = onFloat;
    this.frame = null;
    this.graphics = null;
    this.parts = null;
    this.placed = null;
    document.addEventListener("fullscreenchange", () => this.fullscreenChanged());
    addEventListener("resize", () => { if (this.parts) this.place(this.placed); });
  }

  get docked() { return remember.get(DOCKED) === true; }

  // Keep program docked: a running program moves at once.
  set docked(docked) {
    if (docked) {
      this.undock(true);
    } else {
      this.redock();
    }
  }

  // A new program's frame. takesFocus is false for a restart while the user
  // types (Live, in main.js): a full screen program then offers full screen
  // rather than taking the screen and the keyboard.
  attach(frame, takesFocus = true) {
    this.reset();
    this.frame = frame;
    this.takesFocus = takesFocus;
  }

  // The program's graphics: { width, height, windowed, scaled, title }, with
  // wantsFullscreen when the mode is new and full screen.
  show(graphics) {
    if (!this.frame) return;
    const previous = this.graphics;
    this.graphics = graphics;
    const changed = !previous || previous.windowed !== graphics.windowed ||
      previous.width !== graphics.width || previous.height !== graphics.height;
    if (!graphics.windowed) {
      if (changed && !document.fullscreenElement) {
        if (this.takesFocus && !this.docked) this.enterFullscreen(true);
        else this.offerFullscreen();
      }
    } else if (document.fullscreenElement === this.frame) {
      document.exitFullscreen().catch(() => {});
    }
    // A narrow page has no room for windows, a full screen program plays
    // from the pane, and some prefer the pane.
    if (this.docked || innerWidth < 900 || !graphics.windowed) {
      this.removeParts();
      if (this.docked) this.showUndockButton();
      this.onFloat(false);
      return;
    }
    if (!this.parts) this.open();
    this.parts.title.textContent = graphics.title || "Program";
    this.parts.resize.hidden = !graphics.scaled;
    if (changed) this.place(this.initialPlace());
    this.onFloat(true);
  }

  // Full screen for the frame, now if the browser allows it, and otherwise
  // with a button on the window.
  enterFullscreen(automatic) {
    const frame = this.frame;
    if (!frame) return;
    const allowed = !navigator.userActivation || navigator.userActivation.isActive;
    if (automatic && !allowed) {
      this.offerFullscreen();
      return;
    }
    frame.requestFullscreen().then(() => {
      this.hideOffer();
      frame.focus();
      // So that a tap of Esc reaches the program; holding it leaves.
      if (navigator.keyboard && navigator.keyboard.lock) navigator.keyboard.lock(["Escape"]).catch(() => {});
    }, () => this.offerFullscreen());
  }

  // A full screen program waits in the pane for a click to go full screen.
  offerFullscreen() {
    if (this.parts) {
      this.parts.offer.hidden = false;
      return;
    }
    this.showPaneOffer();
  }

  showPaneOffer() {
    if (this.paneOffer) return;
    this.paneOffer = element("div", "program-offer");
    this.paneOffer.append(button("primary", "Play full screen", "Show the program full screen",
      () => this.enterFullscreen(false)));
    this.pane.append(this.paneOffer);
  }

  hideOffer() {
    if (this.parts) this.parts.offer.hidden = true;
    if (this.paneOffer) {
      this.paneOffer.remove();
      this.paneOffer = null;
    }
  }

  // Esc leaves full screen for the pane, where the program goes on.
  fullscreenChanged() {
    if (document.fullscreenElement) return;
    if (navigator.keyboard && navigator.keyboard.unlock) navigator.keyboard.unlock();
    if (this.frame && !this.parts) this.onFloat(false);
    if (this.frame) this.frame.focus();
  }

  // The window's parts: a title bar, and a resize grip, over the page.
  open() {
    const bar = element("div", "program-titlebar");
    const title = element("span", "title");
    bar.append(title,
      button("fullscreen", "⛶", "Full screen", () => this.enterFullscreen(false)),
      button("dock", "⇲", "Show in the program pane", () => this.undock(true)),
      button("close", "×", "Stop the program", () => this.onClose()));
    const resize = element("div", "program-resize");
    resize.title = "Resize";
    const offer = element("div", "program-offer");
    offer.hidden = true;
    offer.append(button("primary", "Play full screen", "Show the program full screen", () => this.enterFullscreen(false)));
    const note = element("div", "program-away");
    note.append(element("span", "", "The program is running in its own window."),
      button("", "Show it here", "Show the program in this pane", () => this.undock(true)));
    document.body.append(bar, resize, offer);
    this.pane.append(note);
    this.parts = { bar, title, resize, offer, note };
    this.frame.classList.add("floating");
    this.onFloat(true);
    this.drag(bar, (dx, dy, start) => this.place({ ...start, x: start.x + dx, y: start.y + dy }));
    this.drag(resize, (dx, dy, start) => this.place({ ...start, width: start.width + dx, height: start.height + dy }));
  }

  // Moves the frame and its parts: place is { x, y, width, height }, the
  // program's area in CSS pixels, kept on the page.
  place(place) {
    if (!this.parts) return;
    const width = Math.max(160, Math.min(place.width, innerWidth - 2 * MARGIN));
    const height = Math.max(120, Math.min(place.height, innerHeight - TITLE_BAR - 2 * MARGIN));
    const x = Math.min(Math.max(place.x, MARGIN - width + 120), innerWidth - 120);
    const y = Math.min(Math.max(place.y, TITLE_BAR), innerHeight - 40);
    this.placed = { x, y, width, height };
    Object.assign(this.frame.style, { left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` });
    Object.assign(this.parts.bar.style, { left: `${x}px`, top: `${y - TITLE_BAR}px`, width: `${width}px` });
    Object.assign(this.parts.resize.style, { left: `${x + width - 14}px`, top: `${y + height - 14}px` });
    Object.assign(this.parts.offer.style, { left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` });
  }

  // Where a new window goes: the mode's size, scaled down to fit, where the
  // last window was, or else over the right of the page.
  initialPlace() {
    const { width, height } = this.graphics;
    const scale = Math.min(1, (innerWidth - 2 * MARGIN) / width, (innerHeight - TITLE_BAR - 2 * MARGIN) / height);
    const size = { width: Math.round(width * scale), height: Math.round(height * scale) };
    const last = remember.get(PLACE);
    if (last && Number.isFinite(last.x) && Number.isFinite(last.y)) return { ...last, ...size };
    const pane = this.pane.getBoundingClientRect();
    // With the pane hidden (the full-width editor), at the page's right.
    const right = pane.width ? pane.right : innerWidth, top = pane.width ? pane.top : 0;
    return { ...size, x: Math.max(MARGIN, right - size.width - MARGIN * 2),
      y: Math.max(TITLE_BAR + MARGIN, top + TITLE_BAR + MARGIN) };
  }

  // Calls move(dx, dy, start) as the pointer drags handle.
  drag(handle, move) {
    handle.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || event.target.closest("button")) return;
      event.preventDefault();
      handle.setPointerCapture(event.pointerId);
      const start = { ...this.placed }, x = event.clientX, y = event.clientY;
      // The frame would take the pointer from the handle.
      this.frame.style.pointerEvents = "none";
      const moving = (next) => move(next.clientX - x, next.clientY - y, start);
      const done = () => {
        handle.removeEventListener("pointermove", moving);
        handle.removeEventListener("pointerup", done);
        handle.removeEventListener("pointercancel", done);
        if (this.frame) {
          this.frame.style.pointerEvents = "";
          this.frame.focus();
        }
        if (this.placed) remember.set(PLACE, { x: this.placed.x, y: this.placed.y });
      };
      handle.addEventListener("pointermove", moving);
      handle.addEventListener("pointerup", done);
      handle.addEventListener("pointercancel", done);
    });
  }

  // Back in the pane; chosen, it stays there for later programs until the
  // window's button there is used.
  undock(chosen) {
    if (chosen) remember.set(DOCKED, true);
    this.removeParts();
    if (chosen && this.frame) this.showUndockButton();
    if (this.frame) this.frame.focus();
  }

  // A docked window's way back to a window of its own.
  showUndockButton() {
    if (this.undockButton) return;
    this.undockButton = button("program-undock", "⇱ Own window", "Show windowed programs in a window of their own",
      () => this.redock());
    this.pane.append(this.undockButton);
  }

  // The window again, for a docked program.
  redock() {
    remember.set(DOCKED, false);
    if (this.undockButton) {
      this.undockButton.remove();
      this.undockButton = null;
    }
    if (this.graphics && this.graphics.windowed) {
      const graphics = this.graphics;
      this.graphics = null;
      this.show(graphics);
    }
  }

  removeParts() {
    if (this.parts) {
      for (const part of [this.parts.bar, this.parts.resize, this.parts.offer, this.parts.note]) part.remove();
      this.parts = null;
      this.onFloat(false);
    }
    if (this.frame) {
      this.frame.classList.remove("floating");
      for (const name of ["left", "top", "width", "height", "pointerEvents"]) this.frame.style[name] = "";
    }
  }

  // The program has stopped.
  reset() {
    this.removeParts();
    this.hideOffer();
    if (this.undockButton) {
      this.undockButton.remove();
      this.undockButton = null;
    }
    if (this.frame && document.fullscreenElement === this.frame) document.exitFullscreen().catch(() => {});
    this.frame = null;
    this.graphics = null;
    this.placed = null;
  }

  // Where the program is shown: "pane", "window" or "fullscreen".
  get state() {
    if (this.frame && document.fullscreenElement === this.frame) return "fullscreen";
    return this.parts ? "window" : "pane";
  }
}
